import { ExclamationCircleOutlined, MailOutlined, SafetyOutlined, UserDeleteOutlined } from '@ant-design/icons';
import { Alert, Button, Card, Divider, Form, Input, Space, Steps, Typography } from 'antd';
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useDeleteAccountSWR } from '../hooks/useDeleteAccountSWR';
import { useI18n } from '../i18n';
import { useAuth } from '../hooks/useAuth';

const { Title, Paragraph, Text } = Typography;
const { Step } = Steps;

interface DeleteAccountFormData {
  email?: string;
  verificationCode?: string;
}

const DeleteAccountPage: React.FC = () => {
  const navigate = useNavigate();
  const { t } = useI18n();
  const { logout } = useAuth();
  const [form] = Form.useForm();
  const [currentStep, setCurrentStep] = useState(0);

  const {
    loading,
    verificationSent,
    sendVerificationCode,
    resendVerificationCode,
    deleteAccount,
    resetVerificationState,
  } = useDeleteAccountSWR();

  const handleSendVerification = async (values: { email: string }) => {
    const result = await sendVerificationCode({ email: values.email });
    if (result.success) {
      setCurrentStep(1);
    }
  };

  const handleDeleteAccount = async (values: DeleteAccountFormData) => {
    // 从表单中获取email（第一步输入的）
    const email = form.getFieldValue('email');
    const code = values.verificationCode;
    
    if (!email || !code) {
      return;
    }
    
    const result = await deleteAccount({
      email,
      code,
    });
    if (result.success) {
      // 调用logout清除所有认证状态（包括localStorage和Redux）
      logout();
      // 跳转到首页
      navigate('/');
    }
  };

  const handleResendVerification = async () => {
    const email = form.getFieldValue('email');
    if (!email) {
      return;
    }

    await resendVerificationCode({ email });
  };

  return (
    <div style={{
      minHeight: '100vh',
      backgroundColor: '#f5f5f5',
      padding: '104px 0 24px 0' // 增加顶部间距以避免被导航栏遮挡
    }}>
      <div style={{
        maxWidth: '600px',
        margin: '0 auto',
        padding: '0 16px'
      }}>
        <Card>
          <div style={{ textAlign: 'center', marginBottom: '32px' }}>
            <UserDeleteOutlined style={{ fontSize: '48px', color: '#ff4d4f', marginBottom: '16px' }} />
            <Title level={2} style={{ color: '#ff4d4f' }}>
              {t('deleteAccount.title')}
            </Title>
            <Paragraph style={{ color: '#666' }}>
              {t('deleteAccount.subtitle')}
            </Paragraph>
          </div>

          <Steps current={currentStep} style={{ marginBottom: '32px' }}>
            <Step
              title={t('deleteAccount.steps.enterEmail')}
              icon={<MailOutlined />}
            />
            <Step
              title={t('deleteAccount.steps.verifyCode')}
              icon={<SafetyOutlined />}
            />
            <Step
              title={t('deleteAccount.steps.confirm')}
              icon={<ExclamationCircleOutlined />}
            />
          </Steps>

          <Alert
            message={t('deleteAccount.warning.title')}
            description={t('deleteAccount.warning.description')}
            type="warning"
            showIcon
            style={{ marginBottom: '24px' }}
          />

          <Form
            form={form}
            layout="vertical"
            onFinish={currentStep === 0 ? handleSendVerification : handleDeleteAccount}
          >
            {currentStep === 0 && (
              <>
                <Form.Item
                  name="email"
                  label={t('deleteAccount.email.label')}
                  rules={[
                    { required: true, message: t('deleteAccount.email.required') },
                    { type: 'email', message: t('deleteAccount.email.invalid') }
                  ]}
                >
                  <Input
                    placeholder={t('deleteAccount.email.placeholder')}
                    prefix={<MailOutlined />}
                    size="large"
                  />
                </Form.Item>

                <Form.Item>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    block
                    danger
                  >
                    {t('deleteAccount.sendVerification')}
                  </Button>
                </Form.Item>
              </>
            )}

            {currentStep === 1 && (
              <>
                <Form.Item
                  name="verificationCode"
                  label={t('deleteAccount.verification.label')}
                  rules={[
                    { required: true, message: t('deleteAccount.verification.required') },
                    { len: 4, message: t('deleteAccount.verification.length') }
                  ]}
                >
                  <Input
                    placeholder={t('deleteAccount.verification.placeholder')}
                    maxLength={4}
                    size="large"
                    style={{ textAlign: 'center', fontSize: '18px', letterSpacing: '2px' }}
                  />
                </Form.Item>

                <Space direction="vertical" style={{ width: '100%' }}>
                  <Button
                    type="primary"
                    htmlType="submit"
                    loading={loading}
                    size="large"
                    block
                    danger
                  >
                    {t('deleteAccount.confirmDelete')}
                  </Button>

                  <Button
                    onClick={handleResendVerification}
                    loading={loading}
                    size="large"
                    block
                  >
                    {t('deleteAccount.resendVerification')}
                  </Button>

                  <Button
                    onClick={() => {
                      setCurrentStep(0);
                      resetVerificationState();
                      form.resetFields();
                    }}
                    size="large"
                    block
                  >
                    {t('deleteAccount.backToEmail')}
                  </Button>
                </Space>
              </>
            )}
          </Form>

          <Divider />

          <div style={{ textAlign: 'center' }}>
            <Text type="secondary" style={{ fontSize: '12px' }}>
              {t('deleteAccount.footer.note')}
            </Text>
          </div>
        </Card>
      </div>
    </div>
  );
};

export default DeleteAccountPage;
